import { useQuery } from '@tanstack/react-query';

import axios from 'src/lib/axios';
import { QueryConfig } from 'src/lib/react-query';
import { formatResourcePath } from 'src/utils/regex';

import type { ReceiptRow } from 'src/features/payment/utils/receiptRow';

/**
 * The cloud's fiscal note for an online payment.
 *
 * Everything else on this screen reads the on-prem core's financial document,
 * which is a single string. An online payment is taken by the cloud, so its
 * fiscal note - rows, print attributes and the NRA QR - lives there, keyed by the
 * payment order id the cloud sends to the core as payment_source_id.
 */
const RESOURCE = '/payments/:parkingId/payment-orders/:paymentOrderId/digital-receipt/';

type OnlinePaymentReceipt = {
    details: string;
    rows: ReceiptRow[];
    chars_per_line: number;
    status: 'queued' | 'issuing' | 'issued' | 'failed' | 'unavailable';
    unique_sale_id: string | null;
    document_number: string | null;
    qr: string | null;
};

interface DTO {
    parkingId: string;
    paymentOrderId: string;
}

type QueryFnType = typeof queryFn;
const queryFn = ({ parkingId, paymentOrderId }: DTO): Promise<OnlinePaymentReceipt> => {
    return axios.get(formatResourcePath(RESOURCE, { parkingId, paymentOrderId }));
};

interface UseQueryHookOptions extends DTO {
    config?: QueryConfig<QueryFnType>;
}
const useQueryHook = ({ config, ...payload }: UseQueryHookOptions) =>
    useQuery({
        queryKey: [RESOURCE, payload],
        queryFn: async () => await queryFn(payload),
        ...config,
    });

export { useQueryHook as useGetOnlinePaymentReceipt, RESOURCE as GET_ONLINE_PAYMENT_RECEIPT_RESOURCE };
export type { OnlinePaymentReceipt };
